import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, ShieldCheck } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

export default function Footer() {
  const { t, lang } = useI18n();
  const langPrefix = `/${lang}`;
  const privacyPath = lang === "mx" ? "/mx/privacidad" : "/en/privacy";
  const cookiesPath = lang === "mx" ? "/mx/cookies" : "/en/cookies";
  const termsPath = lang === "mx" ? "/mx/terminos" : "/en/terms";

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0B1F32] text-slate-200 pt-16 pb-8"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-4">
          <div>
            <Link
              to={langPrefix}
              data-testid="footer-logo-link"
              className="flex items-center gap-3"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[#059669]">
                <ShieldCheck size={22} strokeWidth={2.4} />
              </span>
              <span className="flex flex-col leading-none">
                <span className="text-lg font-bold">{t("header.brand")}</span>
                <span className="text-[11px] uppercase tracking-widest text-white/60">
                  {t("header.brand_tag")}
                </span>
              </span>
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-slate-300">
              {t("footer.tagline")}
            </p>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-widest text-white/70">
              {t("footer.col_company")}
            </h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link
                  to={langPrefix}
                  data-testid="footer-link-home"
                  className="hover:text-white text-slate-300"
                >
                  {t("footer.link_home")}
                </Link>
              </li>
              <li>
                <a
                  href={`${langPrefix}#apoyo`}
                  data-testid="footer-link-apoyo"
                  className="hover:text-white text-slate-300"
                >
                  {t("footer.link_apoyo")}
                </a>
              </li>
              <li>
                <a
                  href={`${langPrefix}#educacion`}
                  data-testid="footer-link-educacion"
                  className="hover:text-white text-slate-300"
                >
                  {t("footer.link_educacion")}
                </a>
              </li>
              <li>
                <a
                  href={`${langPrefix}#asesoria`}
                  data-testid="footer-link-asesoria"
                  className="hover:text-white text-slate-300"
                >
                  {t("footer.link_asesoria")}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-widest text-white/70">
              {t("footer.col_legal")}
            </h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link
                  to={privacyPath}
                  data-testid="footer-link-privacy"
                  className="hover:text-white text-slate-300"
                >
                  {t("footer.link_privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={cookiesPath}
                  data-testid="footer-link-cookies"
                  className="hover:text-white text-slate-300"
                >
                  {t("footer.link_cookies")}
                </Link>
              </li>
              <li>
                <Link
                  to={termsPath}
                  data-testid="footer-link-terms"
                  className="hover:text-white text-slate-300"
                >
                  {t("footer.link_terms")}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-widest text-white/70">
              {t("footer.col_contact")}
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-300">
              <li className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 shrink-0 text-[#10B981]" />
                <span>{t("footer.contact_address")}</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={16} className="text-[#10B981]" />
                <a
                  href={`mailto:${t("footer.contact_email")}`}
                  className="hover:text-white"
                  data-testid="footer-email"
                >
                  {t("footer.contact_email")}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={16} className="text-[#10B981]" />
                <a
                  href={`tel:${t("footer.contact_phone").replace(/\s/g, "")}`}
                  className="hover:text-white"
                  data-testid="footer-phone"
                >
                  {t("footer.contact_phone")}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Clock size={16} className="mt-0.5 text-[#10B981]" />
                <span>{t("footer.contact_hours")}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 rounded-lg border border-white/10 bg-white/[0.02] p-5 text-xs leading-relaxed text-slate-400">
          <p className="font-semibold text-slate-200">
            {t("footer.legal_company")}
          </p>
          <p className="mt-1">
            {t("footer.legal_rfc")} · {t("footer.legal_condusef")}
          </p>
          <p className="mt-1">{t("footer.legal_incorporation")}</p>
          <p className="mt-1">{t("footer.legal_jurisdiction")}</p>
          <p className="mt-1">{t("footer.legal_cnbv")}</p>
          <p className="mt-3">{t("footer.disclaimer")}</p>
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          {t("footer.rights")}
        </p>
      </div>
    </footer>
  );
}
